import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── Cores por status ──────────────────────────────────────────────────────────
const STATUS_COLOR = {
  // leads
  pre_venda:'#64748b', aguardando_vendedor:'#94a3b8',
  visita:'#fb923c', proposta:'#f6c90e',
  fechado:'#4ade80', perdido:'#f87171', em_espera:'#f59e0b',
  // pós-venda
  aguardando:'#64748b', na_transportadora:'#fb923c', finalizado:'#34d399',
  // engenheiro (novos status)
  aguardando_engenheiro:'#64748b', realizando_planejamento:'#60a5fa',
  aguardando_cemig:'#fbbf24', documentacao_aprovada:'#a78bfa', vistoria_feita:'#4ade80',
  // instalador
  aguardando_instalador:'#64748b', instalacao_programada:'#60a5fa',
  instalando:'#a78bfa', instalacao_concluida:'#4ade80',
  // financeiro
  aguardando_pagamento:'#fbbf24', pago:'#4ade80',
};

// ── Circles V E P I F ─────────────────────────────────────────────────────────
const _VERDE    = new Set(['fechado','finalizado','vistoria_feita','instalacao_concluida','pago','concluido']);
const _VERMELHO = new Set(['aguardando_vendedor','aguardando_engenheiro','aguardando_instalador',
                           'aguardando_pagamento','aguardando_cemig','aguardando','perdido','cancelado']);

function stCor(s) {
  if (!s) return '#1e2d45';
  if (_VERDE.has(s))    return '#4ade80';
  if (_VERMELHO.has(s)) return '#f87171';
  return '#f6c90e';
}

function Circles({ v, e, p, i, f }) {
  const items = [['V', v], ['E', e], ['P', p], ['I', i], ['F', f]];
  return (
    <div style={{ display:'flex', gap:4 }}>
      {items.map(([l, s]) => {
        const c = stCor(s);
        const textColor = s ? '#0d1424' : '#334155';
        return (
          <div key={l} style={{
            width:20, height:20, borderRadius:'50%',
            background: c,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:9, fontWeight:700, color: textColor,
            flexShrink:0,
          }}>{l}</div>
        );
      })}
    </div>
  );
}

// ── Definição dos cargos e seus grupos de status ──────────────────────────────
const CARGO_GROUPS = [
  {
    cargo: 'Vendedor',
    icon: '🤝',
    color: '#f6c90e',
    endpoint: null,
    statusField: 'status',
    clienteIdField: 'cliente_id',
    statuses: [
      { key:'pre_venda',           label:'Pré Venda' },
      { key:'aguardando_vendedor', label:'Aguardando Vendedor' },
      { key:'visita',              label:'Visita Agendada' },
      { key:'proposta',            label:'Proposta Enviada' },
      { key:'fechado',             label:'Venda Fechada' },
      { key:'em_espera',           label:'Aguardando Cliente' },
      { key:'perdido',             label:'Desistiu da Compra' },
    ],
    columns: ['#', 'Cliente', 'Cidade', 'Vendedor', 'Prazo Vendedor', 'Valor da Venda', 'Criado em', 'Pipeline'],
    renderRow: (l, navigate) => [
      `#${l.id}`,
      <button key="n" style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente_id}`)}>{l.cliente_nome}</button>,
      l.cliente_cidade,
      l.vendedor_nome || '—',
      l.prazo_resposta_vendedor
        ? new Date(l.prazo_resposta_vendedor).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })
        : '—',
      l.valor_total_venda
        ? <span key="v" style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : '—',
      new Date(l.criado_em).toLocaleDateString('pt-BR'),
      <Circles key="c" v={l.status} e={l.engenheiro_status} p={l.pos_venda_status} i={l.instalador_status} f={l.financeiro_status} />,
    ],
  },
  {
    cargo: 'Engenheiro',
    icon: '⚙️',
    color: '#a78bfa',
    endpoint: '/engenheiro/',
    statusField: 'status',
    clienteIdField: 'cliente',
    statuses: [
      { key:'aguardando_engenheiro',   label:'Aguardando Engenheiro' },
      { key:'realizando_planejamento', label:'Realizando Planejamento' },
      { key:'aguardando_cemig',        label:'Aguardando CEMIG' },
      { key:'documentacao_aprovada',   label:'Documentação Aprovada' },
      { key:'vistoria_feita',          label:'Vistoria Feita' },
    ],
    columns: ['#', 'Cliente', 'Cidade', 'Vendedor', 'Qtd. Placas', 'Valor da Venda', 'Pipeline'],
    renderRow: (l, navigate) => [
      `#${l.id}`,
      <button key="n" style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente}`)}>{l.cliente_nome}</button>,
      l.cliente_cidade,
      l.vendedor_nome || '—',
      l.quantidade_placas || '—',
      l.valor_total_venda
        ? <span key="v" style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : '—',
      <Circles key="c" v={l.v_status} e={l.status} p={l.p_status} i={l.i_status} f={l.f_status} />,
    ],
  },
  {
    cargo: 'Pós Venda',
    icon: '📦',
    color: '#34d399',
    endpoint: '/pos-venda/',
    statusField: 'status',
    clienteIdField: 'cliente',
    statuses: [
      { key:'aguardando',         label:'Aguardando Pós Venda' },
      { key:'na_transportadora',  label:'Na Transportadora' },
      { key:'instalando',         label:'Instalando' },
      { key:'finalizado',         label:'Finalizado' },
    ],
    columns: ['#', 'Cliente', 'Cidade', 'Vendedor', 'Valor da Venda', 'Pipeline'],
    renderRow: (l, navigate) => [
      `#${l.id}`,
      <button key="n" style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente}`)}>{l.cliente_nome}</button>,
      l.cliente_cidade,
      l.vendedor_nome || '—',
      l.valor_total_venda
        ? <span key="v" style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : '—',
      <Circles key="c" v={l.v_status} e={l.e_status} p={l.status} i={l.i_status} f={l.f_status} />,
    ],
  },
  {
    cargo: 'Instalador',
    icon: '🔧',
    color: '#f97316',
    endpoint: '/instalador/',
    statusField: 'status',
    clienteIdField: 'cliente',
    statuses: [
      { key:'aguardando_instalador',  label:'Aguardando Instalador' },
      { key:'instalacao_programada',  label:'Instalação Programada' },
      { key:'instalando',             label:'Instalando' },
      { key:'instalacao_concluida',   label:'Instalação Concluída' },
    ],
    columns: ['#', 'Cliente', 'Cidade', 'Instalador', 'Valor da Venda', 'Pipeline'],
    renderRow: (l, navigate) => [
      `#${l.id}`,
      <button key="n" style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente}`)}>{l.cliente_nome}</button>,
      l.cliente_cidade,
      l.instalador_nome || '—',
      l.valor_total_venda
        ? <span key="v" style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : '—',
      <Circles key="c" v={l.v_status} e={l.e_status} p={l.p_status} i={l.status} f={l.f_status} />,
    ],
  },
  {
    cargo: 'Financeiro',
    icon: '💰',
    color: '#fbbf24',
    endpoint: '/financeiro/',
    statusField: 'status',
    clienteIdField: 'cliente',
    statuses: [
      { key:'aguardando',           label:'Aguardando Financeiro' },
      { key:'aguardando_pagamento', label:'Aguardando Pagamento' },
      { key:'pago',                 label:'Pago' },
    ],
    columns: ['#', 'Cliente', 'Cidade', 'Vendedor', 'Faturamento', 'Já Pago', 'Lucro', 'Pipeline'],
    renderRow: (l, navigate) => [
      `#${l.id}`,
      <button key="n" style={linkStyle} onClick={() => navigate(`/jornada/${l.cliente}`)}>{l.cliente_nome}</button>,
      l.cliente_cidade,
      l.vendedor_nome || '—',
      l.faturamento_total
        ? <span key="f" style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.faturamento_total).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : '—',
      l.valor_ja_pago
        ? <span key="p" style={{ color:'#4ade80', fontWeight:600 }}>R$ {Number(l.valor_ja_pago).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
        : <span key="p" style={{ color:'#475569' }}>Nenhum</span>,
      l.lucro_percentual != null
        ? <span key="l" style={{ color: l.lucro_percentual >= 0 ? '#4ade80' : '#f87171', fontWeight:600 }}>{l.lucro_percentual}%</span>
        : '—',
      <Circles key="c" v={l.v_status} e={l.e_status} p={l.p_status} i={l.i_status} f={l.status} />,
    ],
  },
];

const S = {
  heading:     { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub:         { fontSize:13, color:'#475569', marginBottom:24 },
  tag:         (c) => ({ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:`${c}22`, color:c, border:`1px solid ${c}44` }),
  searchBox:   { display:'flex', alignItems:'center', gap:8, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', maxWidth:340 },
  searchInput: { background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 },
  th:          { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', whiteSpace:'nowrap' },
  td:          { padding:'11px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117', whiteSpace:'nowrap' },
};

const linkStyle = {
  background:'none', border:'none', color:'#e2e8f0', fontWeight:700,
  cursor:'pointer', padding:0, fontSize:'inherit',
  textDecoration:'underline', textDecorationColor:'rgba(226,232,240,0.3)', textUnderlineOffset:2,
};

// ── Bloco de cargo colapsável ─────────────────────────────────────────────────
function CargoBlock({ cargo, icon, color, statuses, items, columns, renderRow, navigate }) {
  const [open, setOpen] = useState(true);
  const total = items.length;
  if (total === 0) return null;

  return (
    <div style={{ marginBottom:32 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', gap:10,
          background:'#0e1420', border:`1px solid ${color}33`,
          borderRadius:10, padding:'11px 16px', cursor:'pointer',
          marginBottom: open ? 14 : 0,
        }}
      >
        {open
          ? <ChevronDown size={14} color={color} />
          : <ChevronRight size={14} color={color} />}
        <span style={{ fontSize:16 }}>{icon}</span>
        <span style={{ fontSize:15, fontWeight:700, color, flex:1, textAlign:'left' }}>{cargo}</span>
        <span style={S.tag(color)}>{total} registro{total !== 1 ? 's' : ''}</span>
      </button>

      {open && (
        <div style={{ paddingLeft:8 }}>
          {statuses.map(({ key, label }) => {
            const statusItems = items.filter(i => i.status === key);
            if (statusItems.length === 0) return null;
            const sc = STATUS_COLOR[key] || '#64748b';
            return (
              <div key={key} style={{ marginBottom:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <div style={{ width:9, height:9, borderRadius:'50%', background:sc }} />
                  <span style={{ fontSize:13, fontWeight:700, color:sc }}>{label}</span>
                  <span style={S.tag(sc)}>{statusItems.length}</span>
                </div>
                <div style={{ background:'#0e1420', border:`1px solid ${sc}22`, borderRadius:12, overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth: columns.length * 120 }}>
                    <thead>
                      <tr>{columns.map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {statusItems.map((l, idx) => {
                        const cells = renderRow(l, navigate);
                        return (
                          <tr key={l.id ?? idx}
                            onMouseEnter={e => e.currentTarget.style.background='#111827'}
                            onMouseLeave={e => e.currentTarget.style.background=''}>
                            {cells.map((cell, ci) => <td key={ci} style={S.td}>{cell}</td>)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Geral() {
  const navigate = useNavigate();
  const [leads, setLeads]           = useState([]);
  const [posVenda, setPosVenda]     = useState([]);
  const [engenheiro, setEng]        = useState([]);
  const [instalador, setInstalador] = useState([]);
  const [financeiro, setFin]        = useState([]);
  const [search, setSearch]         = useState('');
  const [erro, setErro]             = useState('');

  useEffect(() => {
    const onErr = () => setErro('Erro ao carregar dados. Tente atualizar a página.');
    api.get('/geral/').then(r => setLeads(r.data)).catch(onErr);
    api.get('/pos-venda/').then(r => setPosVenda(r.data)).catch(onErr);
    api.get('/engenheiro/').then(r => setEng(r.data)).catch(onErr);
    api.get('/instalador/').then(r => setInstalador(r.data)).catch(() => {});
    api.get('/financeiro/').then(r => setFin(r.data)).catch(onErr);
  }, []);

  const matchSearch = (nome, cidade, vendedor) => {
    const q = search.toLowerCase();
    return !q ||
      (nome    || '').toLowerCase().includes(q) ||
      (cidade  || '').toLowerCase().includes(q) ||
      (vendedor|| '').toLowerCase().includes(q);
  };

  const filteredLeads      = leads.filter(l => matchSearch(l.cliente_nome, l.cliente_cidade, l.vendedor_nome));
  const filteredPosVenda   = posVenda.filter(l => matchSearch(l.cliente_nome, l.cliente_cidade, l.vendedor_nome));
  const filteredEng        = engenheiro.filter(l => matchSearch(l.cliente_nome, l.cliente_cidade, l.vendedor_nome));
  const filteredInstalador = instalador.filter(l => matchSearch(l.cliente_nome, l.cliente_cidade, l.instalador_nome));
  const filteredFin        = financeiro.filter(l => matchSearch(l.cliente_nome, l.cliente_cidade, l.vendedor_nome));

  const dataMap = {
    'Vendedor':   filteredLeads,
    'Engenheiro': filteredEng,
    'Pós Venda':  filteredPosVenda,
    'Instalador': filteredInstalador,
    'Financeiro': filteredFin,
  };

  return (
    <div>
      <div style={S.heading}>Visão Geral</div>
      <div style={S.sub}>Acompanhe o processo completo da E-solar</div>

      {erro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{erro}</div>
      )}

      <div style={{ ...S.searchBox, marginBottom:24 }}>
        <Search size={14} color="#475569" />
        <input
          style={S.searchInput}
          placeholder="Buscar cliente, vendedor ou cidade..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {CARGO_GROUPS.map(({ cargo, icon, color, statuses, columns, renderRow }) => (
        <CargoBlock
          key={cargo}
          cargo={cargo}
          icon={icon}
          color={color}
          statuses={statuses}
          items={dataMap[cargo] || []}
          columns={columns}
          renderRow={renderRow}
          navigate={navigate}
        />
      ))}
    </div>
  );
}
